<template>
  <quest-main
    title="Localities"
    copy="Management of 'As local as you like it' entities"
    :loading="loading"
  >
    <template v-slot:head>
      <button
        @click="add()"
        class="d-sm-inline-block btn btn-sm btn-primary shadow-sm"
        v-bind="{ disabled: adding }"
      >
        <font-awesome-icon
          icon="plus"
          size="sm"
          fixed-width
          class="text-white-50"
          :class="{ 'animated--heart-beat': adding }"
        ></font-awesome-icon>
        {{ saving ? 'Adding...' : 'Add' }}
      </button>
    </template>
    <template v-slot:body>
      <quest-card title="Localities collection">
        <quest-table id="locality-table" :columns="columns">
          <template v-if="localities.items.length > 0">
            <tr
              class="small align-middle"
              v-for="locality in localities.items"
              :key="locality.id"
            >
              <td>
                <a class="link pointer" @click="promptUpdate(locality)">{{
                  locality.name || '(untitled)'
                }}</a>
              </td>
              <td>{{ lastModified(locality.dateModified) }}</td>
              <td>
                <b-dropdown no-caret variant="link" class="b-dropdown-p-0">
                  <template #button-content>
                    <font-awesome-icon
                      icon="ellipsis-v"
                      size="sm"
                      fixed-width
                      class="text-gray-400"
                    ></font-awesome-icon>
                  </template>
                  <b-dropdown-header>Actions</b-dropdown-header>
                  <b-dropdown-item @click="promptUpdate(locality)"
                    >Update</b-dropdown-item
                  >
                  <b-dropdown-divider></b-dropdown-divider>
                  <b-dropdown-item-button
                    @click="promptDelete(locality)"
                    text="danger"
                    variant="danger"
                    >Delete</b-dropdown-item-button
                  >
                </b-dropdown>
              </td>
            </tr>
          </template>
          <template v-if="localities.items.length === 0">
            <tr class="align-middle">
              <td :colspan="columns.length">
                <div class="small text-center py-5">
                  There are no localities in the system
                </div>
              </td>
            </tr>
          </template>
        </quest-table>
        <quest-pager
          v-if="localities.total > pageSize"
          @changePage="changePage"
          :currentPage="page"
          :itemCount="localities.total"
          :pageSize="pageSize"
        ></quest-pager>
      </quest-card>
      <ValidationObserver ref="vobserver" v-slot="{}">
        <b-modal
          v-model="showUpdateModal"
          centered
          :title="toUpdate && toUpdate.id ? 'Update' : 'Create'"
        >
          <div class="row" v-if="toUpdate">
            <div class="col-md-12">
              <quest-input
                id="name"
                label="Name"
                name="Name"
                v-model="toUpdate.name"
                :rules="{ [REQUIRED]: true }"
                help="The public label for the locality"
              ></quest-input>
            </div>
            <div class="col-md-12">
              <quest-input
                id="landingUrl"
                label="Landing Page URL"
                v-model="toUpdate.landingUrl"
                help="The URL for the landing page"
              ></quest-input>
            </div>
            <div class="col-md-12">
              <quest-single-select
                v-model="toUpdate.themeId"
                :options="themes"
                id="theme"
                label="Theme"
                placeholder="Select a theme"
                help=""
                @update="updateTheme"
              ></quest-single-select>
            </div>
          </div>
          <template v-slot:modal-footer>
            <button
              class="btn btn-secondary btn-sm"
              type="button"
              @click="cancelPromptUpdate"
            >
              Cancel</button
            ><button
              class="btn btn-primary btn-sm"
              type="button"
              @click="confirmPromptUpdate"
            >
              {{ toUpdate && toUpdate.id ? 'Update' : 'Create' }}
            </button>
          </template>
        </b-modal>
      </ValidationObserver>
      <b-modal v-model="showDeleteModal" centered title="Delete">
        <p class="my-4" v-if="toDelete">
          Are you sure you wish to delete locality '{{ toDelete.name }}'?
        </p>
        <template v-slot:modal-footer>
          <button
            class="btn btn-secondary btn-sm"
            type="button"
            @click="cancelPromptDelete"
          >
            Cancel</button
          ><button
            class="btn btn-danger btn-sm"
            type="button"
            @click="confirmPromptDelete"
          >
            Delete
          </button>
        </template>
      </b-modal>
    </template>
  </quest-main>
</template>

<script>
import { REQUIRED } from '@/validation-types'
import { success, error } from '../helpers'
import { QuestInput, QuestSingleSelect } from './form'
import moment from 'moment'
import 'moment-timezone'
import {
  QuestMain,
  QuestTable,
  QuestCard,
  QuestModal,
  QuestPager,
} from './structure'
import { LocalityService, ThemeService } from '../services'
import PagedResult from '../models/paged-result'

export default {
  name: 'localities',
  components: {
    QuestMain,
    QuestTable,
    QuestCard,
    QuestPager,
    QuestSingleSelect,
    QuestModal,
    QuestInput,
  },
  data() {
    return {
      REQUIRED,
      pageSize: 10,
      page: 1,
      term: '',
      localities: new PagedResult(),
      columns: ['Name', 'Modified', ''],
      loading: true,
      adding: false,
      saving: false,
      toUpdate: null,
      toDelete: null,
      showUpdateModal: false,
      showDeleteModal: false,
      themes: [],
    }
  },
  mounted() {
    new ThemeService()
      .all()
      .then((data) => (this.themes = data))
      .finally((_) => this.load())
  },
  methods: {
    new() {
      return {
        id: null,
        name: '',
        themeId: '',
      }
    },
    add() {
      this.promptUpdate(this.new())
    },
    lastModified(date) {
      return moment(`${date}Z`).tz('Australia/Melbourne').format('lll')
    },
    updateTheme(val) {
      if (this.toUpdate) {
        this.toUpdate.themeId = val
      }
    },
    load() {
      this.changePage(1)
    },
    changePage(page) {
      new LocalityService()
        .list(page, this.pageSize, this.term)
        .then((data) => {
          this.localities = data
          this.page = page
        })
        .finally((_) => (this.loading = false))
    },
    update() {
      new LocalityService()
        .update(this.toUpdate)
        .then((data) => {
          if (data) {
            this.changePage(1)
            success(`Updated locality #`, this.toUpdate.name)
          } else {
            error(`Failed to update locality #`, this.toUpdate.name)
          }
        })
        .finally((_) => {
          this.showUpdateModal = false
          this.toUpdate = {}
        })
    },
    create() {
      new LocalityService()
        .add(this.toUpdate)
        .then((data) => {
          if (data) {
            this.changePage(1)
            success(`Added locality #`, this.toUpdate.name)
          } else {
            error(`Failed to add locality #'`, this.toUpdate.name)
          }
        })
        .finally((_) => {
          this.showUpdateModal = false
          this.toUpdate = {}
        })
    },
    promptUpdate(locality) {
      this.toUpdate = Object.assign({}, locality)
      this.showUpdateModal = true
    },
    promptDelete(locality) {
      this.toDelete = locality
      this.showDeleteModal = true
    },
    cancelPromptUpdate() {
      this.showUpdateModal = false
      this.toUpdate = {}
    },
    cancelPromptDelete() {
      this.showDeleteModal = false
      this.toDelete = null
    },
    async confirmPromptUpdate() {
      if (!this.$refs.vobserver) return
      let validated = await this.$refs.vobserver.validate()
      if (!validated) {
        error('Please ensure all errors are resolved')
        return
      }
      if (this.toUpdate.id) {
        this.update()
      } else {
        this.create()
      }
    },
    confirmPromptDelete() {
      new LocalityService()
        .delete(this.toDelete.id)
        .then((data) => {
          this.showDeleteModal = false

          if (data) {
            this.changePage(1)
            success(`Deleted locality #`, this.toDelete.name)
          } else {
            error(`Failed to delete locality #`, this.toDelete.name)
          }
        })
        .finally((_) => {
          this.toDelete = null
        })
    },
  },
}
</script>
